// The "Working as" ui (Shipping hub, Phase 1, 2026-10-27): a desktopUi-shaped object whose PIN questions
// are answered by the operator session (features/operatorSession/session.js) instead of a fresh PIN each time.
//
//   identity prompts  (who is doing this: seal, ship, stage, pickup, send, receive...: any ui.pin call
//                      WITHOUT face:false, and ui.identity(why)) -> the live session's token; with no live
//                      session the person signs in first: pinPromptWithOptions({autofill:false, face:true}).
//                      A face match's 16-digit token IS the session (its expiry from /check); a typed PIN
//                      goes to POST /auth/operator-session.
//   approval prompts  (opts.face === false: a manager's override, an admin's OK) -> a typed prompt every time,
//                      through the base ui (autofill off): a session never approves anything.
//
// The admin session (sessionStorage adminPin) is never read: autofill is off on every prompt made here.
import { pinPromptWithOptions } from '../../utils/pinPrompt';
import { clearSession, liveSession, readSession, writeSession } from './session';
import * as sessionApi from './sessionApi';

/** A face token is good for 10 minutes from the match; when /check can't say, assume 9 (never longer). */
export const FACE_FALLBACK_MS = 9 * 60 * 1000;

const WHY = {
  seal: 'seal it',
  ship: 'ship it',
  stage: 'mark it ready for pickup',
  pickup: 'confirm the pickup',
  send: 'send it to the vendor',
  receive: 'receive it back',
  load: 'record the load',
  create: 'make the manifest',
  edit: 'change the manifest',
};

/** The sign-in question. `actionMessage` (the action's own PIN question) wins over `why`. */
export function signInMessage(why, actionMessage = null) {
  if (actionMessage) return `${actionMessage}\nYou stay signed in on this screen for 10 minutes.`;
  const doing = why ? (WHY[why] || why) : null;
  const lead = doing ? `Before you ${doing}: who is working?` : 'Who is working?';
  return `${lead}\nEnter your PIN. You stay signed in on this screen for 10 minutes.`;
}

/** jobq-api answers these when it can't keep sessions (503 "Sign-in sessions aren't set up yet", or an API without
 *  the route): the typed PIN then answers the one action that asked, and nothing is kept. */
const NO_SESSIONS = new Set([404, 405, 503]);

/** The deps a test (or the phone, later) can swap. */
const DEFAULT_DEPS = { prompt: pinPromptWithOptions, api: sessionApi, now: () => Date.now() };

let pending = null; // one sign-in at a time per tab: two actions asking at once share it

/**
 * Sign in (or switch person). Resolves the new session, or null when the person backed out or JobQ refused
 * (the refusal is shown through `alert`). `oneAction` (an action asked): when JobQ can't keep sessions the typed
 * PIN is handed back as {token: pin, transient: true} for that action only.
 */
export function signIn({ why = null, message = null, alert = null, oneAction = false, deps = DEFAULT_DEPS } = {}) {
  if (pending) return pending;
  const d = { ...DEFAULT_DEPS, ...deps };
  pending = (async () => {
    const answer = await d.prompt(signInMessage(why, message), { autofill: false, face: true });
    if (!answer || !answer.pin) return null;
    if (answer.face) {
      // the face token is already a session: ask JobQ when it ends
      const c = await d.api.checkSession(answer.pin);
      if (!c.ok && c.expired) {
        if (alert) await alert(c.message || 'That sign-in has already run out. Try again.');
        return null;
      }
      const info = c.ok ? c.info : {};
      return writeSession({
        token: answer.pin,
        user_id: info.user_id ?? answer.face.user_id,
        name: info.name || answer.face.name,
        role: info.role || null,
        source: 'face',
        expires_at: info.expires_at || new Date(d.now() + FACE_FALLBACK_MS).toISOString(),
      });
    }
    const r = await d.api.startSession(answer.pin);
    if (!r.ok && oneAction && NO_SESSIONS.has(r.status)) return { token: answer.pin, transient: true };
    if (!r.ok) {
      if (alert) await alert(r.message || 'Sign-in failed.');
      return null;
    }
    return writeSession(r.session);
  })();
  return pending.finally(() => { pending = null; });
}

/** End this tab's session (JobQ is told; if it can't be reached the token just runs out). */
export async function signOut({ deps = DEFAULT_DEPS } = {}) {
  const d = { ...DEFAULT_DEPS, ...deps };
  const s = readSession();
  clearSession();
  if (s) await d.api.endSession(s.token);
}

/** Switch person: the old token ends, the next person signs in. */
export async function switchPerson({ alert = null, deps = DEFAULT_DEPS } = {}) {
  await signOut({ deps });
  return signIn({ alert, deps });
}

/** Re-read the stored session against JobQ (a revoked token: PIN changed, person deactivated). */
export async function verifySession({ deps = DEFAULT_DEPS } = {}) {
  const d = { ...DEFAULT_DEPS, ...deps };
  const s = liveSession(d.now());
  if (!s) return null;
  const c = await d.api.checkSession(s.token);
  // the person may have signed out or switched while JobQ answered: never write back over that
  const now = readSession();
  if (!now || now.token !== s.token) return now;
  if (!c.ok) {
    if (c.expired) { clearSession(); return null; }
    return s; // JobQ not reachable: keep it until it runs out
  }
  const info = c.info || {};
  return writeSession({ ...s, name: info.name || s.name, role: info.role || s.role, expires_at: info.expires_at || s.expires_at });
}

/** The token for an identity question: the live session's, else a sign-in first. null = backed out. */
export async function identityToken(why, { message = null, alert = null, deps = DEFAULT_DEPS } = {}) {
  const d = { ...DEFAULT_DEPS, ...deps };
  const s = liveSession(d.now());
  if (s) return s.token;
  const n = await signIn({ why, message, alert, oneAction: true, deps });
  return n ? n.token : null;
}

/** baseUi (desktopUi / phoneUi shape) with its PIN questions answered by the Working-as session. */
export function workingAsUi(baseUi, deps = DEFAULT_DEPS) {
  const alert = baseUi && baseUi.alert ? (m) => baseUi.alert(m) : null;
  const identity = (why) => identityToken(why, { alert, deps: { ...DEFAULT_DEPS, ...deps } });
  return {
    ...baseUi,
    workingAs: true,
    identity,
    pin: (message, opts) => {
      if (opts && opts.face === false) {
        // an approval: typed every time, never the session, never the admin session
        return baseUi.pin(message, { ...opts, autofill: false, face: false });
      }
      return identityToken(null, { message, alert, deps: { ...DEFAULT_DEPS, ...deps } });
    },
  };
}
